import {
  Button,
  makeStyles,
  mergeClasses,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "@fluentui/react-components";
import { InfoRegular } from "@fluentui/react-icons";
import { useContext, useEffect, useState } from "react";
import { NoDataCard } from "../../../components-v2/common/no-data-card/NoDataCard";
import { NotificationContext } from "../../../context/NotificationContext";
import { $sky60 } from "../../../cssjs/colors";
import { useApiResource } from "../../../hooks/useApiResource";
import {
  IKpiSettingsData,
  IKpiSettingsResponse,
} from "../../../models/app-settings/AppSettingsModels";
import { IRollingTime } from "../../../models/common/DateTimeModels";
import {
  convertKPIFetchData,
  getKpiSettingsPayload,
} from "../../../utils/app-settings/AppSettingsHelper";
import { scrollToTop } from "../../../utils/common/CommonHelperFunctions";
import {
  AIRPORT_CODE_PARAMETER,
  EMPTY_STATE_MESSAGE,
  MESSAGEBAR_TYPE,
} from "../../../utils/common/Constants";
import FligthKPIRollingInput from "./FligthKPIRollingInput";

interface IFlightKPISettingsProps {
  airportCode: string;
}

const kpiSettingsRawURL = process.env.REACT_APP_API_GET_KPI_TIME;
const baseURL = process.env.REACT_APP_API_BASE_URL;
const kpiSettingsURL = `${baseURL}${kpiSettingsRawURL}`;

const FlightKPISettings: React.FC<IFlightKPISettingsProps> = ({
  airportCode,
}) => {
  const styles = useStyles();
  const { addNotification } = useContext(NotificationContext);

  const [kpiRows, setKpiRows] = useState<IKpiSettingsData[]>([]);

  const url = kpiSettingsURL.replace(AIRPORT_CODE_PARAMETER, airportCode);
  const {
    data,
    isLoading,
    isError: isFetchError,
    refetch,
    update,
    isUpdating,
    isUpdateSuccess,
    isUpdateError,
  } = useApiResource<IKpiSettingsResponse>({
    queryKey: ["kpiSettings", airportCode],
    url: url,
    method: "PUT",
  });

  const kpiSettingsData = data?.kpiSettings || [];

  useEffect(() => {
    if (kpiSettingsData.length > 0) {
      setKpiRows(convertKPIFetchData(kpiSettingsData));
    }
  }, [kpiSettingsData]);

  useEffect(() => {
    if (isUpdateSuccess) {
      addNotification(
        MESSAGEBAR_TYPE.WARNING,
        "Reload all open APOC tabs to apply changes"
      );
      addNotification(
        MESSAGEBAR_TYPE.SUCCESS,
        `KPI Config Settings saved successfully`
      );
    } else if (isUpdateError) {
      addNotification(
        MESSAGEBAR_TYPE.ERROR,
        "Failed to update KPI Config Settings"
      );
    }
  }, [isUpdateSuccess, isUpdateError]);

  const handleCheckboxChange = (index: number) => {
    const updated = [...kpiRows];
    updated[index].hidden = !updated[index].hidden;
    setKpiRows(updated);
  };

  const toggleActiveRollingTime = (index: number) => {
    const updated = [...kpiRows];
    updated[index].isRollingActive = !updated[index].isRollingActive;
    setKpiRows(updated);
  };

  const updateRollingTime = (
    index: number,
    rollingTime: IRollingTime,
    isError: boolean
  ) => {
    const updatedRows = [...kpiRows];
    updatedRows[index].fromHour = rollingTime.fromHour;
    updatedRows[index].fromMinute = rollingTime.fromMinute;
    updatedRows[index].toHour = rollingTime.toHour;
    updatedRows[index].toMinute = rollingTime.toMinute;
    updatedRows[index].isError = isError;
    setKpiRows(updatedRows);
  };

  const onCancelHandler = async () => {
    if (isUpdating) {
      return;
    }

    const result = await refetch();
    if (result?.data && result.data?.kpiSettings) {
      setKpiRows(convertKPIFetchData(result.data.kpiSettings));
    }
  };

  const onSaveHandler = () => {
    if (isUpdating) {
      return;
    }

    const kpiSettingsPayload = getKpiSettingsPayload(kpiRows);
    update(kpiSettingsPayload);
    scrollToTop();
  };

  const isError = kpiRows.some((row: any) => row.isError);

  const tableBodyContent = kpiRows.map((row: any, index: number) => {
    const rollingTime = {
      fromHour: row.fromHour,
      fromMinute: row.fromMinute,
      toHour: row.toHour,
      toMinute: row.toMinute,
    };

    return (
      <TableRow
        key={row.id}
        className={mergeClasses(styles.row, styles.rowBorders)}
      >
        <TableCell className={styles.cell}>{row.label}</TableCell>

        <TableCell className={styles.cell}>
          <TableCellLayout>
            <FligthKPIRollingInput
              rowIndex={index}
              isRollingTimeActive={row.isRollingActive}
              toggleActiveRollingTime={() => toggleActiveRollingTime(index)}
              rollingTime={rollingTime}
              updateRollingTime={updateRollingTime}
            />
          </TableCellLayout>
        </TableCell>
      </TableRow>
    );
  });

  return (
    <div className={styles.containerHeight}>
      {isLoading && (
        <Spinner
          labelPosition="below"
          label="Loading..."
          className="margin-auto-all"
        />
      )}
      {isFetchError && (
        <div className="margin-auto-all">
          <NoDataCard message={EMPTY_STATE_MESSAGE.Data} />
        </div>
      )}
      {!isFetchError && !isLoading && (
        <>
          <header className="fs-16 fw-400 pb-sm-4 amd-container-vcenter gap-5">
            <InfoRegular />
            Use below configuration to hide/unhide KPI and set rolling time in
            reference to current date time.
          </header>
          <div className={styles.container}>
            <Table>
              <TableHeader>
                <TableRow
                  className={mergeClasses(styles.headerRow, styles.rowBorders)}
                >
                  <TableHeaderCell style={{ width: "100px" }}>
                    KPI Name
                  </TableHeaderCell>
                  <TableHeaderCell style={{ width: "180px" }}>
                    Custom Value | Rolling time (hrs)
                  </TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>{tableBodyContent}</TableBody>
            </Table>
          </div>
          <footer className="d-flex pt-6 pb-1 gap-10">
            <Button onClick={onCancelHandler}>Cancel</Button>
            <Button
              appearance="primary"
              disabled={isError}
              className={styles.button}
              onClick={onSaveHandler}
            >
              {isUpdating ? "Saving..." : "Save"}
            </Button>
          </footer>
        </>
      )}
    </div>
  );
};

export default FlightKPISettings;

const useStyles = makeStyles({
  container: {
    border: "1px solid #000000B5",
    borderRadius: "5px",
    width: "clamp(700px, 800px, 100%)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  headerRow: {
    borderBottom: "1px solid #0000007D",
    height: "50px",
    "& > th": {
      fontWeight: "500",
    },
  },
  row: {
    borderBottom: "1px solid #CCCCCC",
    ":last-child": {
      borderBottom: "none",
    },
  },
  rowBorders: {
    "& > *:not(:last-child)": {
      borderRight: "1px solid rgba(0, 0, 0, 0.50)",
    },
  },
  cell: {
    padding: "8px",
    verticalAlign: "middle",
    width: "80px",
    ":first-child": {
      borderRadius: "0 0 0 5px",
    },
    ":last-child": {
      borderRadius: "0 0 5px 0",
    },
  },
  alignCellItems: {
    verticalAlign: "middle",
    textAlign: "center",
  },
  button: {
    "&:disabled": {
      border: `1px solid ${$sky60} !important`,
    },
  },
  containerHeight: {
    display: "flex",
    flexDirection: "column",
    minHeight: "518px",
  },
});
